import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom';
import { WREG_ROUTE, wregCarriedSearch, wregStepFromPath } from '@/components/webreg/wregRoutes';
import type { WregStep } from '@/components/webreg/wregSteps';

export const WREG_DEPTH_KEY = 'wregDepth';

type WregLocationState = { [WREG_DEPTH_KEY]?: number } | null;

export type WregNav = {
  step: WregStep;
  canBack: boolean;
  go: (next: WregStep) => void;
  replace: (next: WregStep) => void;
  back: () => void;
  reset: (next?: WregStep) => void;
};

let navigatedBack = false;

export const consumeWregNavigatedBack = (): boolean => {
  const value = navigatedBack;
  navigatedBack = false;
  return value;
};

export const wregDepthOf = (state: unknown): number => {
  const depth = (state as WregLocationState)?.[WREG_DEPTH_KEY];
  return typeof depth === 'number' && Number.isFinite(depth) && depth > 0 ? depth : 0;
};

export const useWregNav = (): WregNav => {
  const location = useLocation();
  const navigate = useNavigate();

  const navRef = useRef<NavigateFunction>(navigate);
  useEffect(() => {
    navRef.current = navigate;
  });

  const step = wregStepFromPath(location.pathname) ?? 'intake';
  const depth = wregDepthOf(location.state);

  const search = wregCarriedSearch(location.search);
  const searchRef = useRef(search);
  useEffect(() => {
    searchRef.current = search;
  });

  const depthRef = useRef(depth);
  useEffect(() => {
    depthRef.current = depth;
  });

  const to = useCallback(
    (next: WregStep, nextDepth: number, replace: boolean) => {
      navRef.current(
        { pathname: WREG_ROUTE[next], search: searchRef.current },
        { replace, state: { [WREG_DEPTH_KEY]: nextDepth } },
      );
    },
    [],
  );

  const go = useCallback((next: WregStep) => to(next, depthRef.current + 1, false), [to]);

  const replace = useCallback((next: WregStep) => to(next, depthRef.current, true), [to]);

  const back = useCallback(() => {
    navigatedBack = true;
    navRef.current(-1);
  }, []);

  const reset = useCallback((next: WregStep = 'intake') => to(next, 0, true), [to]);

  return { step, canBack: depth > 0, go, replace, back, reset };
};
